"use client";

import isEqual from "lodash/isEqual";
import dynamic from "next/dynamic";
import { memo, useEffect, useRef } from "react";

import { MessageProp } from "@/context/redux/reducers/chat-slice";
import { AvatarImage } from "@/features/winwingpt/chat/components/AvatarImage";

const MessageWrapper = dynamic(() => import("./MessageWrapper"), {
  ssr: false,
});

const TextContent = dynamic(() => import("./TextContent"), {
  ssr: false,
});

export const MessageComponent = memo(
  ({ session }: { session: MessageProp[] }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const prevSessionRef = useRef(session);
    const isManualScrollingRef = useRef(false);
    const isAutoScrollingRef = useRef(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const scrollDown = () => {
      setTimeout(() => {
        if (isManualScrollingRef.current) return;

        if (containerRef.current) {
          containerRef.current.scrollTop =
            containerRef.current.scrollHeight -
            containerRef.current.clientHeight;
        }
      }, 400);
    };

    useEffect(() => {
      // Only scroll if new messages are added
      if (isEqual(prevSessionRef.current, session)) return;

      if (!isAutoScrollingRef.current) isAutoScrollingRef.current = true;
      prevSessionRef.current = session;
      scrollDown();

      // Reset auto scrolling after 500ms
      timeoutRef.current = setTimeout(() => {
        isManualScrollingRef.current = false;
        isAutoScrollingRef.current = false;
      }, 1000);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }, [session]);

    const handleManualScroll = () => {
      if (isAutoScrollingRef.current) isManualScrollingRef.current = true;
    };

    if (session.length === 0) return null;

    return (
      <div
        onWheel={handleManualScroll}
        onTouchStart={handleManualScroll}
        ref={containerRef}
        className="flex flex-col gap-5 overflow-auto scroll-smooth"
      >
        {session.map((message, index) => {
          switch (message.type) {
            case "client":
              return (
                <MessageWrapper
                  key={index}
                  contentAlign="right"
                  data-testid="client-message"
                >
                  <TextContent variant="client">{message.content}</TextContent>
                </MessageWrapper>
              );
            case "gpt":
              return (
                <MessageWrapper
                  key={index}
                  contentAlign="left"
                  avatar={<AvatarImage />}
                  data-testid="gpt-response"
                >
                  <TextContent variant="server">{message.content}</TextContent>
                </MessageWrapper>
              );
            case "element":
              return (
                <div key={index} className="w-full">
                  {message.content}
                </div>
              );
            case "chart":
              return (
                <MessageWrapper
                  key={index}
                  contentAlign="left"
                  avatar={<AvatarImage />}
                >
                  {message.content}
                </MessageWrapper>
              );
            default:
              return null;
          }
        })}
      </div>
    );
  }
);
MessageComponent.displayName = "MessageComponent";
